/* -------------------------- Design imports start -------------------------- */
import { FormControl, FormHelperText, TextField } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement } from "react";

import LogTool from '../../logger/logTools'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  id?: string
  value: string | number
  limits?: { minInput?: number; maxInput?: number }
  label: string
  labelWidth?: number
  onChange: (value: number | string) => void
  validation?: boolean
  helperText?: string
  errorMessage?: string
  disabled?: boolean
  required?: boolean
  showError?: boolean
}
/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function NumberInput(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    id,
    label,
    value,
    onChange,
    disabled = false,
    required = false,
    errorMessage = '',
    validation = true,
    showError = true,
  } = props
  let { limits = { minInput: Number.MIN_SAFE_INTEGER, maxInput: Number.MAX_SAFE_INTEGER } } = props

  const log = new LogTool({ context: 'NumberInput', enable: false })
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  // const [error, setError] = useState<boolean>(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  // useEffect(() => {
  //   if(required && value === '') setError(true)
  // }, [value, validation])
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  function isValidInput(input: string | number): boolean {
    if (typeof input === 'undefined') {
      log.debug(`isValidInput(${input})`, 'Input is undefined, return true.')
      return true
    }
    
    // Convert input to string if it's a number
    const inputStr = typeof input === 'number' ? input.toString() : input
    
    if (inputStr === '') {
      log.debug(`isValidInput(${inputStr})`, 'Input is empty string, return true.')
      return true
    }
    if (!/^[0-9]+((.|,)[0-9]+)?$/.test(inputStr)) {
      log.debug(
        `isValidInput(${inputStr})`,
        'Input does not only contain numbers and decimal delimiters, return false.'
      )
      return false
    }

    const parsedInput = parseFloat(inputStr.replace(',', '.'))
    if (Number.isNaN(parsedInput)) {
      log.debug(`isValidInput(${input})`, 'Input is NaN, return false.')
      return false
    }
    
    // Check if value is within limits
    const hasMinLimit = limits.minInput !== undefined && limits.minInput !== Number.MIN_SAFE_INTEGER
    const hasMaxLimit = limits.maxInput !== undefined && limits.maxInput !== Number.MAX_SAFE_INTEGER
    
    if (hasMinLimit && parsedInput < limits.minInput!) {
      log.debug(`isValidInput(${input})`, `Input ${parsedInput} is below minimum ${limits.minInput}, return false.`)
      return false
    }
    
    if (hasMaxLimit && parsedInput >= limits.maxInput!) {
      log.debug(`isValidInput(${input})`, `Input ${parsedInput} is at or above maximum ${limits.maxInput}, return false.`)
      return false
    }
    
    log.debug(`isValidInput(${input})`, 'Input is valid, return true.')
    return true
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const error = (validation && !isValidInput(value)) || (required && !value)
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  // parse limits
  if (!Object.hasOwn(limits, 'minInput')) {
    limits = { minInput: Number.MIN_SAFE_INTEGER, maxInput: limits.maxInput }
  }
  if (!Object.hasOwn(limits, 'maxInput')) {
    limits = { minInput: limits.minInput, maxInput: Number.MAX_SAFE_INTEGER }
  }
  /* ------------------------- Pre render actions end ------------------------- */

  log.debug('GLOBAL', 'value ->', value, ', error ->', error)

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <FormControl fullWidth variant="outlined">
      <TextField
        type="text" // if number is used the input label shrinks even if value !== ''
        //inputMode='numeric'
        variant="outlined"
        label={label}
        id={id || label}
        helperText={props.helperText}
        value={value || ''}
        onChange={e => {
          onChange(e.currentTarget.value)
          log.debug(
            `onChange(${e.currentTarget.value})`,
            'Typeof ->',
            typeof e.currentTarget.value,
            ', parseFloat(value) ->',
            parseFloat(e.currentTarget.value)
          )
          // validation && setError(!isValidInput(e.currentTarget.value))
        }}
        autoComplete="number"
        disabled={disabled}
        required={required}
        error={error && showError}
        aria-describedby="text-input"
        inputProps={{
          'aria-label': 'weight',
          /* defaultValue: '1',
          min: '0',
          step: '1', */
        }}
      />
      {error && showError && (
        <FormHelperText error variant="outlined">
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  )
}
